import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  Button,
  Alert,
  Dialog,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Chip,
} from '@mui/material';
import {
  Message as SmsIcon,
  Error as AlertCircle,
  Info as InfoIcon,
} from '@mui/icons-material';
import { SmsFilterForm } from '@/components/sms/SmsFilterForm';
import { SmsStatusTracker } from '@/components/sms/SmsStatusTracker';
import { IndividualSmsGrid } from '@/components/sms/IndividualSmsGrid';
import {
  fetchSmsFilterOptionsRequested,
  smsTriggerPreviewRequested,
  smsTriggerSendRequested,
  smsTriggerStatusRequested,
  setSmsCurrentFilters,
  clearSmsPreview,
  clearSmsStatus,
  resetSmsTrigger,
  fetchPoliciesForSelectionRequested,
  togglePolicySelection,
  selectAllPolicies,
  clearPolicySelection,
  triggerIndividualSmsRequested,
  setPolicyFilters,
  setIndividualSelectionMode
} from '@/store/slices/smsTriggerSlice';
import { SmsTriggerFilters, SmsPolicyFilters } from '@/types/sms';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageLayout } from '@/components/ui/PageLayout';

export default function SmsTrigger() {
  const dispatch = useDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [bulkConfirmDialogOpen, setBulkConfirmDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const lastSearchQueryRef = useRef<string>('');
  
  const {
    filterOptions,
    filterOptionsLoading,
    filterOptionsError,
    preview,
    previewLoading,
    previewError,
    triggerResponse,
    triggerLoading,
    triggerError,
    status,
    statusLoading,
    statusError,
    currentFilters,
    isPreviewVisible,
    isStatusVisible,
    isIndividualSelectionMode,
    policies,
    policiesLoading,
    policiesError,
    selectedPolicyNumbers,
    policyFilters,
    paginationInfo
  } = useSelector((state: RootState) => state.smsTrigger);

  // Load filter options on component mount
  useEffect(() => {
    dispatch(fetchSmsFilterOptionsRequested());
    
    // Cleanup on unmount
    return () => {
      dispatch(resetSmsTrigger());
    };
  }, [dispatch]);

  const handleFiltersChange = useCallback((filters: SmsTriggerFilters) => {
    dispatch(setSmsCurrentFilters(filters));
    // Clear preview when filters change
    if (isPreviewVisible) {
      dispatch(clearSmsPreview());
    }
    // Clear search query when filters are cleared (empty filters object)
    if (Object.keys(filters).length === 0) {
      setSearchQuery('');
    }
  }, [dispatch, isPreviewVisible]);

  const handleSearch = useCallback((filters: SmsTriggerFilters) => {
    // When search is clicked, fetch policies for display
    // Convert SmsTriggerFilters to SmsPolicyFilters format
    const policyFilters: SmsPolicyFilters = {
      lob: filters.lob,
      product_code: filters.product_code,
      product_name: filters.product_name,
      date_range: filters.date_range,
      red_from: filters.red_from,
      red_to: filters.red_to,
      month: filters.month,
      policy_number: filters.policy_number,
      channel: filters.channel,
      sub_channel: filters.sub_channel,
      customer_mobile: filters.customer_mobile,
      sms_type: filters.sms_type || 'notice_only',
      search: searchQuery || undefined,
      page: 1,
      page_size: 10
    };
    
    // Also get preview for count in bulk mode
    if (!isIndividualSelectionMode) {
      dispatch(smsTriggerPreviewRequested(filters));
    }
    
    // Fetch policies for table display
    dispatch(fetchPoliciesForSelectionRequested(policyFilters));
  }, [dispatch, isIndividualSelectionMode, searchQuery]);

  const handleSendSms = useCallback(() => {
    // Show confirmation dialog instead of directly sending
    setBulkConfirmDialogOpen(true);
  }, []);

  const handleConfirmBulkSms = useCallback(() => {
    setBulkConfirmDialogOpen(false);
    if (currentFilters) {
      // Include search query in filters if active
      const filtersWithSearch = searchQuery 
        ? { ...currentFilters, search: searchQuery }
        : currentFilters;
      dispatch(smsTriggerSendRequested(filtersWithSearch));
    }
  }, [dispatch, currentFilters, searchQuery]);

  const handleCancelBulkSms = useCallback(() => {
    setBulkConfirmDialogOpen(false);
  }, []);

  const handleRefreshStatus = useCallback(() => {
    if (triggerResponse?.batch_id) {
      dispatch(smsTriggerStatusRequested(triggerResponse.batch_id));
    }
  }, [dispatch, triggerResponse]);

  const handleDownloadReport = useCallback(async () => {
    if (!status?.batch_id) {
      console.error('No batch ID available for download');
      return;
    }

    try {
      // Import the download function dynamically
      const { downloadSmsErrorLog } = await import('@/services/api');
      await downloadSmsErrorLog(status.batch_id);
    } catch (error) {
      console.error('Error downloading report:', error);
      // Could show an error toast/alert here
    }
  }, [status?.batch_id]);

  const handleClearStatus = useCallback(() => {
    dispatch(clearSmsStatus());
  }, [dispatch]);

  // Individual Selection handlers
  const handleTogglePolicySelection = useCallback((policyNumber: string) => {
    dispatch(togglePolicySelection(policyNumber));
  }, [dispatch]);

  const handleSelectAll = useCallback(() => {
    dispatch(selectAllPolicies());
  }, [dispatch]);

  const handleClearSelection = useCallback(() => {
    dispatch(clearPolicySelection());
  }, [dispatch]);

  const handleTriggerIndividual = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmTrigger = useCallback(() => {
    setConfirmDialogOpen(false);
    dispatch(triggerIndividualSmsRequested(selectedPolicyNumbers));
  }, [dispatch, selectedPolicyNumbers]);

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    dispatch(setPolicyFilters({ page, page_size: pageSize }));
    // Convert currentFilters to policyFilters format for pagination
    const updatedPolicyFilters: SmsPolicyFilters = {
      lob: currentFilters.lob,
      product_code: currentFilters.product_code,
      product_name: currentFilters.product_name,
      date_range: currentFilters.date_range,
      red_from: currentFilters.red_from,
      red_to: currentFilters.red_to,
      month: currentFilters.month,
      policy_number: currentFilters.policy_number,
      channel: currentFilters.channel,
      sub_channel: currentFilters.sub_channel,
      customer_mobile: currentFilters.customer_mobile,
      sms_type: currentFilters.sms_type || 'notice_only',
      search: searchQuery || undefined,
      page,
      page_size: pageSize
    };
    dispatch(fetchPoliciesForSelectionRequested(updatedPolicyFilters));
  }, [dispatch, currentFilters, searchQuery]);

  const handleSearchChange = useCallback((query: string) => {
    // Always allow empty query to trigger API call (to clear search)
    // Only prevent duplicate calls if query is non-empty and hasn't changed
    if (query && lastSearchQueryRef.current === query) {
      return;
    }
    lastSearchQueryRef.current = query;
    
    // Update state
    setSearchQuery(query);
    
    // Reset to page 1 when search changes
    const pageSize = policyFilters.page_size || 10;
    dispatch(setPolicyFilters({ page: 1, page_size: pageSize }));
    
    // Convert currentFilters to policyFilters format with search
    const updatedPolicyFilters: SmsPolicyFilters = {
      lob: currentFilters.lob,
      product_code: currentFilters.product_code,
      product_name: currentFilters.product_name,
      date_range: currentFilters.date_range,
      red_from: currentFilters.red_from,
      red_to: currentFilters.red_to,
      month: currentFilters.month,
      policy_number: currentFilters.policy_number,
      channel: currentFilters.channel,
      sub_channel: currentFilters.sub_channel,
      customer_mobile: currentFilters.customer_mobile,
      sms_type: currentFilters.sms_type || 'notice_only',
      search: query || undefined, // Empty string becomes undefined (no search param)
      page: 1,
      page_size: pageSize
    };
    dispatch(fetchPoliciesForSelectionRequested(updatedPolicyFilters));
  }, [dispatch, currentFilters, policyFilters.page_size]);

  const handleModeToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setIndividualSelectionMode(event.target.checked));
    // Clear selection when switching modes
    if (!event.target.checked) {
      dispatch(clearPolicySelection());
    }
  }, [dispatch]);

  // Help content for unified interface
  const helpContent = (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        How to Use SMS Trigger:
      </Typography>
      <Box component="ol" sx={{ paddingLeft: 2, margin: 0 }}>
        <Box component="li" sx={{ marginBottom: 0.5, fontSize: '13px' }}>
          <strong>Set Filters:</strong> Use the filter form to specify which policies to search for
        </Box>
        <Box component="li" sx={{ marginBottom: 0.5, fontSize: '13px' }}>
          <strong>Search:</strong> Click "Search" to find matching policies and view them in the table
        </Box>
        <Box component="li" sx={{ marginBottom: 0.5, fontSize: '13px' }}>
          <strong>Choose Mode:</strong> Toggle "Select Individual Policies" to switch between bulk and individual selection
        </Box>
        <Box component="li" sx={{ marginBottom: 0.5, fontSize: '13px' }}>
          <strong>Send SMS:</strong> In bulk mode, click "Send to All". In individual mode, select policies and click "SMS Trigger"
        </Box>
        <Box component="li" sx={{ marginBottom: 0.5, fontSize: '13px' }}>
          <strong>Track Progress:</strong> Monitor the real-time status of SMS processing and view detailed results
        </Box>
      </Box>
    </Box>
  );

  // Create SMS Type and Individual Selection controls for page header
  const pageHeaderAction = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {/* SMS Type Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          SMS Type:
        </Typography>
        <RadioGroup
          row
          value={currentFilters.sms_type || 'notice_only'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const updatedFilters = { ...currentFilters, sms_type: e.target.value as 'notice_only' | 'notice_with_link' };
            dispatch(setSmsCurrentFilters(updatedFilters));
          }}
          sx={{ margin: 0 }}
        >
          <FormControlLabel
            value="notice_only"
            control={<Radio size="small" />}
            label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Notice Only</Typography>}
            sx={{ margin: 0, marginRight: 1 }}
          />
          <FormControlLabel
            value="notice_with_link"
            control={<Radio size="small" />}
            label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Notice + Link</Typography>}
            sx={{ margin: 0 }}
          />
        </RadioGroup>
      </Box>

      {/* Mode Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isIndividualSelectionMode || false}
              onChange={handleModeToggle}
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
              Individual Selection
            </Typography>
          }
          sx={{ margin: 0 }}
        />
        {isIndividualSelectionMode ? (
          <Chip 
            label="Individual" 
            color="primary" 
            size="small" 
            variant="outlined"
            sx={{ height: '24px', fontSize: '0.75rem' }}
          />
        ) : (
          <Chip 
            label="Bulk" 
            color="default" 
            size="small" 
            variant="outlined"
            sx={{ height: '24px', fontSize: '0.75rem' }}
          />
        )}
      </Box>

      <Tooltip 
        title={helpContent}
        arrow
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 400,
              bgcolor: 'rgba(97, 97, 97, 0.95)',
              '& .MuiTooltip-arrow': {
                color: 'rgba(97, 97, 97, 0.95)',
              },
            },
          },
        }}
      >
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          <InfoIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <PageLayout>
      <PageHeader
        icon={<SmsIcon />}
        title="SMS Trigger"
        action={pageHeaderAction}
      />

      {/* Global Error Display */}
      {filterOptionsError && (
        <Alert severity="error" sx={{ marginBottom: 1.5 }} icon={<AlertCircle />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Error</Typography>
          <Typography variant="body2">{filterOptionsError}</Typography>
        </Alert>
      )}

      {triggerError && (
        <Alert severity="error" sx={{ marginBottom: 1.5 }} icon={<AlertCircle />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Error</Typography>
          <Typography variant="body2">{triggerError}</Typography>
        </Alert>
      )}

      {/* Unified Interface Content */}
      {/* Loading Overlay for Initial Load */}
      {filterOptionsLoading && !filterOptions && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        }}>
          <CircularProgress size={60} sx={(theme) => ({ color: theme.palette.primary.main })} />
          <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: 'text.secondary', margin: 0 }}>
            Loading SMS Trigger Interface...
          </Typography>
          <Typography variant="body2" sx={(theme) => ({ fontSize: '14px', color: theme.palette.grey[400], margin: 0 })}>
            Fetching filter options and initializing the system
          </Typography>
        </Box>
      )}

      {/* Filter Form */}
      <Box sx={{ marginBottom: 1.5 }}>
        <SmsFilterForm
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          filterOptionsError={filterOptionsError}
          onFiltersChange={handleFiltersChange}
          onPreview={handleSearch}
          previewLoading={previewLoading || policiesLoading}
          currentFilters={currentFilters}
        />
      </Box>

      {/* Error Display for Policies */}
      {policiesError && (
        <Alert severity="error" sx={{ marginBottom: 1.5 }} icon={<AlertCircle />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Error</Typography>
          <Typography variant="body2">{policiesError}</Typography>
        </Alert>
      )}

      {/* Results Grid - Show after search (when paginationInfo exists) or when loading/error */}
      {(paginationInfo !== null || policiesLoading || policiesError) ? (
        <Box sx={{ marginBottom: 1.5 }}>
          <IndividualSmsGrid
            policies={policies}
            selectedPolicyIds={selectedPolicyNumbers}
            onToggleSelection={handleTogglePolicySelection}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onTriggerSms={handleTriggerIndividual}
            onSendToAll={handleSendSms}
            isIndividualSelectionMode={isIndividualSelectionMode}
            loading={policiesLoading}
            error={policiesError}
            onSearchChange={handleSearchChange}
            pagination={paginationInfo}
            onPageChange={handlePageChange}
            totalCount={searchQuery ? paginationInfo?.total : (preview?.count || paginationInfo?.total)}
            smsSendableCount={searchQuery ? paginationInfo?.sms_sendable_count : (preview?.count || paginationInfo?.sms_sendable_count)}
          />
        </Box>
      ) : null}

      {/* Status Tracking Section */}
      {isStatusVisible && (
        <Box sx={{ marginBottom: 1.5, animation: 'fadeIn 0.5s' }}>
          <SmsStatusTracker
            status={status}
            statusLoading={statusLoading}
            statusError={statusError}
            triggerResponse={triggerResponse}
            onRefresh={handleRefreshStatus}
          />
        </Box>
      )}

      {/* Confirmation Dialog for Individual Selection */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 10000 }} // Set z-index above all dropdowns (9999)
        PaperProps={{
          sx: {
            maxWidth: 720,
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            p: 3,
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Confirm SMS Trigger</Typography>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary" size="small">
            Close
          </Button>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to send SMS to {selectedPolicyNumbers.length} selected policies?
            <br />
            <br />
            <strong>SMS Type:</strong> {(currentFilters.sms_type || 'notice_only') === 'notice_with_link' 
              ? 'Renewal Notice + Journey Link' 
              : 'Renewal Notice Only'}
            <br />
            <br />
            This action will trigger SMS notifications immediately.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmTrigger} variant="contained" sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}>
            Confirm & Send SMS
          </Button>
        </Box>
      </Dialog>

      {/* Confirmation Dialog for Bulk SMS */}
      <Dialog 
        open={bulkConfirmDialogOpen} 
        onClose={() => setBulkConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 10000 }} // Set z-index above all dropdowns (9999)
        PaperProps={{
          sx: {
            maxWidth: 720,
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            p: 3,
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Confirm Bulk SMS Trigger</Typography>
          <Button onClick={() => setBulkConfirmDialogOpen(false)} color="primary" size="small">
            Close
          </Button>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will send SMS to {searchQuery ? (paginationInfo?.sms_sendable_count ?? paginationInfo?.total ?? 0) : (preview?.count || 0)} policies matching your filter criteria.
            This action will trigger SMS notifications immediately and cannot be undone.
            Do you want to continue?
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={() => setBulkConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmBulkSms} variant="contained" sx={(theme) => ({ backgroundColor: theme.palette.primary.main, color: 'white' })}>
            Yes, Send SMS to {searchQuery ? (paginationInfo?.sms_sendable_count ?? paginationInfo?.total ?? 0) : (preview?.count || 0)} Policies
          </Button>
        </Box>
      </Dialog>
    </PageLayout>
  );
}

